import React from 'react'
import { history } from '../store'
import { connect } from 'react-redux'
import AvailabilityChips from './AvailabilityChips'
import FaultsAnalysisModal, {
  buildAvailabilityByCustomer,
  downloadFaultAnalysisExcel,
  downloadFaultAnalysisPDF,
} from './FaultsAnalysisModal'
import { resolveFaultCode, BROKEN_LOCK_PINCHO, BROKEN_LOCK_PESTILLO } from '../utils/faults'

// Badge color scale for the fault percentage (already rounded to 1 decimal):
// 0% green, 0.1–3% orange, 3.1–4.9% brown, ≥5% red.
function faultPctColor(pct) {
  if (pct === 0) return '#16a34a'
  if (pct <= 3) return '#f59e0b'
  if (pct < 5) return '#8B4513'
  return '#dc3545'
}

class FaultsByCustomerList extends React.Component {
  constructor(props) {
    super(props);

    this.state = { analysisCustomer: null }

    this.handleDownloadExcel = this.handleDownloadExcel.bind(this)
    this.handleDownloadPDF = this.handleDownloadPDF.bind(this)
    this.openAnalysis = this.openAnalysis.bind(this)
    this.closeAnalysis = this.closeAnalysis.bind(this)
  }

  goTo(Serial, e) {
    history.push(`/machines/${Serial}`)
  }

  handleDownloadExcel(customer) {
    downloadFaultAnalysisExcel(this.props.matrixs, this.props.machines, customer)
  }

  handleDownloadPDF(customer) {
    downloadFaultAnalysisPDF(this.props.matrixs, this.props.machines, customer)
  }

  openAnalysis(customer) {
    this.setState({ analysisCustomer: customer })
  }

  closeAnalysis() {
    this.setState({ analysisCustomer: null })
  }

  renderFaultList() {
    const { matrixs, machines } = this.props;

    // Create an object to store fault objects by customer name
    const faultsByCustomer = {};

    // Iterate over matrixs and machines to populate faultsByCustomer object
    matrixs.forEach((matrix) => {
      const serial = matrix.Serial;
      const machine = machines.find((machine) => machine.Serial === serial);

      if (machine) {
        const customer = machine.Customer;
        const customerName = customer && customer.Fullname ? customer.Fullname : '';

        matrix.Locker.forEach((lock) => {
          if (lock.FaultCode !== undefined && lock.Status === 'FL') {
            const faultCode = resolveFaultCode(lock.FaultCode, lock.HardType);
            const key = customerName;

            if (!faultsByCustomer[key]) {
              faultsByCustomer[key] = {
                customer: customerName,
                [BROKEN_LOCK_PINCHO]: 0,
                [BROKEN_LOCK_PESTILLO]: 0,
                GREEN_BLOCK: 0,
                RED_BLOCK: 0,
                PINPAD_BLOCK: 0,
                DOOR_BLOCK: 0,
                PINPAD_ACTIVE: 0,
                TOTAL_FAULTS: 0,
              };
            }

            // Codes are counted by key, so one with no counter above — a fault
            // type added later — would tally to NaN unless seeded first.
            if (faultsByCustomer[key][faultCode] === undefined) {
              faultsByCustomer[key][faultCode] = 0;
            }

            faultsByCustomer[key][faultCode]++;
            faultsByCustomer[key].TOTAL_FAULTS++;
          }
        });
      }
    });

    // Locker availability per customer: averías, taquillas en negro and sin
    // batería — a wider net than the faults tallied above, but not the PDF's
    // TOTAL INCIDENCIAS either, since low battery still opens.
    const availabilityByCustomer = buildAvailabilityByCustomer(matrixs, machines);

    // Convert faultsByCustomer object to an array of fault objects, annotating
    // each with the fault/locker ratio shared by the badge and the ≥5% filter.
    // Customers with no known locker total never count as critical.
    const faultList = Object.values(faultsByCustomer).map((fault) => {
      const availability = availabilityByCustomer.get(fault.customer);
      const totalLockers = availability ? availability.total : 0;
      // Rounded to 1 decimal so display, color scale, sort and the ≥5% filter
      // all agree on the same number (e.g. 4.96% shows as 5.0% AND counts as red).
      const faultPct = totalLockers > 0 ? Math.round((fault.TOTAL_FAULTS / totalLockers) * 1000) / 10 : null;
      return {
        ...fault,
        availability,
        totalLockers,
        faultPct,
        critical: faultPct !== null && faultPct >= 5,
      };
    });

    // Sort per the header controls; ties fall back to alphabetical ascending
    // regardless of direction so equal counts stay in a readable order.
    const { sortBy, sortDir } = this.props;
    const dir = sortDir === 'desc' ? -1 : 1;
    faultList.sort((a, b) => {
      let cmp;
      if (sortBy === 'faults') {
        cmp = a.TOTAL_FAULTS - b.TOTAL_FAULTS;
      } else if (sortBy === 'pct') {
        cmp = (a.faultPct || 0) - (b.faultPct || 0);
      } else {
        cmp = a.customer.localeCompare(b.customer);
      }
      return dir * cmp || a.customer.localeCompare(b.customer);
    });

    const filter = (this.props.filterText || '').trim().toLowerCase();
    let filteredList = filter ? faultList.filter(f => (f.customer || '').toLowerCase().includes(filter)) : faultList;
    if (this.props.criticalOnly) {
      filteredList = filteredList.filter(f => f.critical);
    }

    return filteredList.map((fault) => (
      <div key={fault.customer} className="list-group-item fault-card d-flex align-items-start justify-content-between">
        <div style={{ flex: 1 }}>
          <div className="font-weight-bold">
            {fault.customer || '—'}
            {fault.faultPct !== null && (
              <span
                className="badge ml-2"
                style={{ backgroundColor: faultPctColor(fault.faultPct), color: '#fff' }}
                title={`${fault.TOTAL_FAULTS} averías de ${fault.totalLockers} taquillas`}
              >
                {fault.critical && <i className="fas fa-exclamation-triangle mr-1"></i>}
                {fault.faultPct.toFixed(1)}% averías
              </span>
            )}
          </div>
          <div className="text-muted small">Total Averías: {fault.TOTAL_FAULTS}</div>
          <div className="mt-2 fault-chips" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <span className="badge badge-light">Pinchos: {fault[BROKEN_LOCK_PINCHO]}</span>
            <span className="badge badge-light">Pestillos: {fault[BROKEN_LOCK_PESTILLO]}</span>
            <span className="badge badge-light">Pin verde: {fault.GREEN_BLOCK}</span>
            <span className="badge badge-light">Pin rojo: {fault.RED_BLOCK}</span>
            <span className="badge badge-light">Teclado: {fault.PINPAD_BLOCK}</span>
            <span className="badge badge-light">Puerta: {fault.DOOR_BLOCK}</span>
            <span className="badge badge-light">T. encendido: {fault.PINPAD_ACTIVE}</span>
          </div>
          <AvailabilityChips availability={fault.availability} className="mt-2" />
        </div>

        <div style={{ marginLeft: 12, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'stretch', minWidth: 150 }}>
          <button
            className="btn btn-sm"
            onClick={() => this.openAnalysis(fault.customer)}
            style={{ background: 'linear-gradient(135deg, #6d28d9, #a855f7)', color: '#fff', border: 'none' }}
          >
            <i className="fas fa-chart-bar mr-1"></i>Análisis
          </button>
          <button
            className="btn btn-sm"
            onClick={() => this.handleDownloadExcel(fault.customer)}
            style={{ background: 'linear-gradient(135deg, #1d6f42, #22a35c)', color: '#fff', border: 'none' }}
          >
            <i className="fas fa-file-excel mr-1"></i>Excel
          </button>
          <button
            className="btn btn-sm"
            onClick={() => this.handleDownloadPDF(fault.customer)}
            style={{ background: 'linear-gradient(135deg, #b91c1c, #ef4444)', color: '#fff', border: 'none' }}
          >
            <i className="fas fa-file-pdf mr-1"></i>PDF
          </button>
        </div>
      </div>
    ));
  }

  render() {
    if (!this.props.matrixs) {
      return null
    }

    return (
      <div id="content-to-pdf">
        <div className="list-group">
          {this.renderFaultList()}
        </div>
        <FaultsAnalysisModal
          open={!!this.state.analysisCustomer}
          customer={this.state.analysisCustomer}
          matrixs={this.props.matrixs}
          machines={this.props.machines}
          onClose={this.closeAnalysis}
        />
      </div>
    )
  }
}

export default connect(null, null)(FaultsByCustomerList);

